import { requestGetPicture } from "@/state/picture/PictureEffects";
import { Picture } from "@/type/core/offices/offices.types";
import { useEffect, useState } from "react";
import { getToken } from "@utils/helpers/api.helper";
import { toastError, toastSuccess } from "@utils/helpers/toast-helper";
import { blobToBase64 } from "@utils/helpers/base64-helper";
import { useTranslation } from "react-i18next";
import { Image } from "@/type/core/image/image.types";
import {
  requestDeleteUserPicture,
  requestUpdateUserPicture,
} from "@/state/users/UserEffects";
import { useUserPictureContext } from "@utils/contexts/UserPictureContext";
import { deleteUserPicture } from "@/state/auth/AuthEvents";

interface UseUserPictureReturn {
  isUserPictureLoading: boolean;
  avatar?: Image;
  setAvatar?: (avatar?: Image) => void;
  fetchedProfilePicture?: Image;
  handleSubmitAvatarUpdate: (rank: number) => void;
  cancelAvatarUpdate: () => void;
}

interface UseUserPictureProps {
  userPictures?: Picture[];
}

const useUserPicture = ({
  userPictures,
}: UseUserPictureProps): UseUserPictureReturn => {
  const { t } = useTranslation();

  const [isUserPictureLoading, setIsUserPictureLoading] =
    useState<boolean>(false);
  const { avatar, setAvatar } = useUserPictureContext();
  const [fetchedProfilePicture, setFetchedProfilePicture] = useState<
    Image | undefined
  >(avatar);

  const pictures: string[] =
    userPictures && userPictures.length > 0
      ? (userPictures[0].picture ?? "").replace("/picture/", "").split("?")
      : [];

  const pictureId = pictures.length > 0 ? pictures[0] : undefined;

  useEffect(() => {
    if (pictureId) {
      setIsUserPictureLoading(true);
      void requestGetPicture({ id: pictureId, token: getToken() });
    }
  }, [pictureId]);

  useEffect(() => {
    return requestGetPicture.doneData.watch((result) => {
      if (!result.ok) {
        // 404 here just mean that the user has no avatar picture because he can't find picture/{id} where id is undefined (just lead to /picture)
        if (result.responseCode !== 404) {
          toastError(t("error.toast.userPicture.get"));
        }
      }
      const blob = result.data;
      if (blob) {
        blobToBase64(blob)
          .then((base64Data) => {
            const type = blob.type;
            const title = t("account.myInformations.profilePicture");
            const image: Image = {
              file: blob,
              base64Data,
              type,
              name: title,
              alt: title,
              title: title,
            };
            setFetchedProfilePicture(image);
            setAvatar(image);
          })
          .catch(() => toastError(t("error.toast.blobToBase64")))
          .finally(() => setIsUserPictureLoading(false));
      } else {
        setIsUserPictureLoading(false);
      }
    });
  });

  const handleSubmitAvatarUpdate = (rank: number) => {
    if (!avatar) {
      void requestDeleteUserPicture({ dto: { rank, picture: true } })
        .then((response) => {
          if (response.ok) {
            setFetchedProfilePicture?.(avatar);
            deleteUserPicture();
            toastSuccess(t("success.avatar.deleted"));
          } else {
            toastError(t("error.toast.avatar.deleted"));
          }
        })
        .catch(() => {
          toastError(t("error.toast.avatar.deleted"));
        });
    } else {
      const formData = new FormData();
      formData.append("picture", avatar?.file || "none");
      formData.append(
        "rank",
        new Blob([`${rank}`], { type: "application/json" }),
        "",
      );
      void requestUpdateUserPicture({ dto: formData })
        .then((response) => {
          if (response.ok) {
            setFetchedProfilePicture?.(avatar);
            toastSuccess(t("success.avatar.modified"));
          } else {
            toastError(t("error.toast.avatar.modified"));
          }
        })
        .catch(() => {
          toastError(t("error.toast.avatar.modified"));
        });
    }
  };

  const cancelAvatarUpdate = () => {
    setAvatar?.(fetchedProfilePicture);
  };

  return {
    isUserPictureLoading,
    avatar,
    setAvatar,
    fetchedProfilePicture,
    handleSubmitAvatarUpdate,
    cancelAvatarUpdate,
  };
};

export default useUserPicture;
